import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusCircleIcon,
    FunnelIcon,
    ViewColumnsIcon,
    RocketLaunchIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    BoltIcon,
    MegaphoneIcon
} from '@heroicons/react/24/outline';

const PageContainer = styled.div`
  /* Match ProjectForm - no special container styling */
`;

const Breadcrumb = styled.nav`
  margin-bottom: 1rem;
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 0.5rem;
  color: var(--gray-400);
`;

const BreadcrumbText = styled.span`
  color: var(--gray-600);
`;

const CardContainer = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.$isDark ? '#374151' : '#e5e7eb'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 3px solid #EF4444;
`;

const HeaderLeft = styled.div`
  h2 {
    color: ${props => props.$isDark ? '#f9fafb' : 'var(--gray-900)'};
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0 0 0.25rem 0;
  }

  p {
    color: ${props => props.$isDark ? '#9ca3af' : 'var(--gray-600)'};
    margin: 0;
    font-size: 0.875rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.variant === 'secondary' ? (props.$isDark ? '#374151' : 'white') : 'var(--primary-600)'};
  color: ${props => props.variant === 'secondary' ? (props.$isDark ? '#e5e7eb' : 'var(--gray-700)') : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${props.$isDark ? '#4b5563' : 'var(--gray-300)'}` : 'none'};
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${props => props.variant === 'secondary' ? (props.$isDark ? '#4b5563' : 'var(--gray-50)') : 'var(--primary-700)'};
  }
`;

const Controls = styled.div`
  background: transparent;
  border-radius: 0;
  padding: 0;
  margin-bottom: 24px;
  box-shadow: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const DateNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  button {
    width: 36px;
    height: 36px;
    border: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-300)'};
    background: ${props => props.$isDark ? '#374151' : 'white'};
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    color: ${props => props.$isDark ? '#e5e7eb' : 'inherit'};

    &:hover {
      background: ${props => props.$isDark ? '#4b5563' : 'var(--gray-50)'};
      border-color: ${props => props.$isDark ? '#6b7280' : 'var(--gray-400)'};
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .current-month {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.$isDark ? '#f9fafb' : 'var(--gray-900)'};
    min-width: 200px;
    text-align: center;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => props.active ? (props.color || 'var(--primary-600)') : (props.$isDark ? '#4b5563' : 'var(--gray-300)')};
  background: ${props => props.active ? (props.color || 'var(--primary-50)') : (props.$isDark ? '#374151' : 'white')};
  color: ${props => props.active ? 'white' : (props.$isDark ? '#e5e7eb' : 'var(--gray-700)')};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${props => props.active ? (props.color || 'var(--primary-600)') : (props.$isDark ? '#4b5563' : 'var(--gray-50)')};
  }
`;

const CalendarContainer = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.$isDark ? '#374151' : '#e5e7eb'};
  overflow: hidden;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const DayHeader = styled.div`
  padding: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.$isDark ? '#e5e7eb' : 'var(--gray-700)'};
  background: ${props => props.$isDark ? '#374151' : 'var(--gray-50)'};
  border-bottom: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-200)'};
`;

const DayCell = styled.div`
  min-height: 140px;
  padding: 8px;
  border-right: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-200)'};
  border-bottom: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-200)'};
  background: ${props => props.isOtherMonth 
    ? (props.$isDark ? '#111827' : 'var(--gray-50)') 
    : (props.$isDark ? '#1f2937' : 'white')};
  position: relative;
  transition: background 0.2s;

  &:hover {
    background: ${props => props.isOtherMonth 
      ? (props.$isDark ? '#1f2937' : 'var(--gray-100)') 
      : (props.$isDark ? '#374151' : 'var(--gray-50)')};
  }

  &:nth-child(7n) {
    border-right: none;
  }

  .day-number {
    font-size: 14px;
    font-weight: ${props => props.isToday ? '700' : '500'};
    color: ${props => props.isToday ? 'white' : props.isOtherMonth ? (props.$isDark ? '#6b7280' : 'var(--gray-400)') : (props.$isDark ? '#f9fafb' : 'var(--gray-900)')};
    margin-bottom: 6px;
    background: ${props => props.isToday ? 'var(--primary-600)' : 'transparent'};
    width: ${props => props.isToday ? '28px' : 'auto'};
    height: ${props => props.isToday ? '28px' : 'auto'};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${props => props.isToday ? '50%' : '0'};
  }
`;

const ActivityItem = styled.div`
  padding: 4px 8px;
  margin-bottom: 4px;
  background: ${props => props.color}20;
  border-left: 3px solid ${props => props.color};
  border-radius: 4px;
  font-size: 12px;
  color: ${props => props.$isDark ? '#e5e7eb' : 'var(--gray-800)'};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${props => props.color}40;
    transform: translateX(2px);
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }

  .activity-title {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${props => props.$isDark ? '#374151' : 'white'};
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .stat-label {
    font-size: 13px;
    color: ${props => props.$isDark ? '#9ca3af' : 'var(--gray-600)'};
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: var(--primary-600);
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 16px 20px;
  background: ${props => props.$isDark ? '#374151' : 'var(--gray-50)'};
  border-top: 1px solid ${props => props.$isDark ? '#4b5563' : 'var(--gray-200)'};

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${props => props.$isDark ? '#e5e7eb' : 'var(--gray-700)'};

    .color-box {
      width: 16px;
      height: 16px;
      border-radius: 3px;
    }
  }
`;

// Activity type configuration
const ACTIVITY_TYPES = {
    campaign: {
        label: 'Campaigns',
        color: '#3B82F6',
        icon: RocketLaunchIcon
    },
    blog: {
        label: 'Blog Posts',
        color: '#10B981',
        icon: DocumentTextIcon
    },
    email: {
        label: 'Email/Social',
        color: '#F59E0B',
        icon: EnvelopeIcon
    },
    automation: {
        label: 'Automation',
        color: '#6B7280',
        icon: BoltIcon
    },
    event: {
        label: 'Events',
        color: '#8B5CF6',
        icon: MegaphoneIcon
    }
};

const MarketingCalendar = () => {
    const { isDark } = useTheme();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedFilters, setSelectedFilters] = useState(Object.keys(ACTIVITY_TYPES));
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadActivities();
    }, [currentDate]);

    const loadActivities = async () => {
        setLoading(true);
        try {
            // Get the first and last day of the current month
            const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

            // In a real implementation, this would call the API
            // For now, we'll use mock data
            const mockActivities = generateMockActivities(firstDay, lastDay);
            setActivities(mockActivities);
        } catch (error) {
            console.error('Error loading activities:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateMockActivities = (startDate, endDate) => {
        const activities = [];
        const types = Object.keys(ACTIVITY_TYPES);

        // Generate ~15 random activities
        for (let i = 0; i < 15; i++) {
            const randomDay = new Date(
                startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime())
            );
            const type = types[Math.floor(Math.random() * types.length)];

            activities.push({
                id: `activity_${i}`,
                type,
                title: getActivityTitle(type, i),
                date: randomDay.toISOString().split('T')[0],
                status: ['scheduled', 'draft', 'published'][Math.floor(Math.random() * 3)]
            });
        }

        return activities;
    };

    const getActivityTitle = (type, index) => {
        const titles = {
            campaign: ['Black Friday Sale', 'Product Launch', 'Summer Campaign', 'Newsletter Signup'],
            blog: ['SEO Best Practices', 'Marketing Tips', 'Industry Insights', 'How-to Guide'],
            email: ['Weekly Newsletter', 'Product Update', 'Customer Survey', 'Event Invitation'],
            automation: ['Welcome Series', 'Cart Abandonment', 'Re-engagement', 'Lead Nurture'],
            event: ['Webinar', 'Product Demo', 'Conference', 'Workshop']
        };

        return titles[type]?.[index % titles[type].length] || 'Activity';
    };

    const changeMonth = (delta) => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
    };

    const toggleFilter = (filter) => {
        setSelectedFilters(prev =>
            prev.includes(filter)
                ? prev.filter(f => f !== filter)
                : [...prev, filter]
        );
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Calendar rendering logic
    const getDaysInMonth = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month's days
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: prevMonthDays - i,
                month: month - 1,
                year: month === 0 ? year - 1 : year,
                isOtherMonth: true,
                fullDate: new Date(year, month - 1, prevMonthDays - i)
            });
        }

        // Current month's days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: i,
                month,
                year,
                isOtherMonth: false,
                fullDate: new Date(year, month, i)
            });
        }

        // Next month's days
        const remainingDays = 42 - days.length; // 6 rows × 7 days
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: i,
                month: month + 1,
                year: month === 11 ? year + 1 : year,
                isOtherMonth: true,
                fullDate: new Date(year, month + 1, i)
            });
        }

        return days;
    };

    const getActivitiesForDate = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return activities.filter(
            activity => activity.date === dateStr && selectedFilters.includes(activity.type)
        );
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const days = getDaysInMonth();
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Calculate stats
    const totalActivities = activities.filter(a => selectedFilters.includes(a.type)).length;
    const scheduledActivities = activities.filter(a => a.status === 'scheduled' && selectedFilters.includes(a.type)).length;
    const publishedActivities = activities.filter(a => a.status === 'published' && selectedFilters.includes(a.type)).length;

    const navigate = useNavigate();

    return (
        <PageContainer>
            <Breadcrumb>
                <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
                    Dashboard
                </button>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
                <BreadcrumbText>Marketing Calendar</BreadcrumbText>
            </Breadcrumb>

            <CardContainer $isDark={isDark}>
                <Header>
                    <HeaderLeft $isDark={isDark}>
                        <h2>📅 Marketing Calendar</h2>
                        <p>Unified view of all your marketing activities and schedules</p>
                    </HeaderLeft>
                    <HeaderActions>
                        <Button variant="secondary" onClick={goToToday} $isDark={isDark}>
                            <CalendarIcon />
                            Today
                        </Button>
                        <Button $isDark={isDark}>
                            <PlusCircleIcon />
                            New Activity
                        </Button>
                    </HeaderActions>
                </Header>

            <Stats>
                <StatCard $isDark={isDark}>
                    <div className="stat-label">
                        <CalendarIcon />
                        Total Activities
                    </div>
                    <div className="stat-value">{totalActivities}</div>
                </StatCard>

                <StatCard $isDark={isDark}>
                    <div className="stat-label">
                        <RocketLaunchIcon />
                        Scheduled
                    </div>
                    <div className="stat-value">{scheduledActivities}</div>
                </StatCard>

                <StatCard $isDark={isDark}>
                    <div className="stat-label">
                        <DocumentTextIcon />
                        Published
                    </div>
                    <div className="stat-value">{publishedActivities}</div>
                </StatCard>
            </Stats>

            <Controls>
                <DateNavigation $isDark={isDark}>
                    <button onClick={() => changeMonth(-1)}>
                        <ChevronLeftIcon />
                    </button>
                    <div className="current-month">{monthName}</div>
                    <button onClick={() => changeMonth(1)}>
                        <ChevronRightIcon />
                    </button>
                </DateNavigation>

                <FilterGroup>
                    {Object.entries(ACTIVITY_TYPES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                            <FilterChip
                                key={key}
                                active={selectedFilters.includes(key)}
                                color={config.color}
                                onClick={() => toggleFilter(key)}
                                $isDark={isDark}
                            >
                                <Icon />
                                {config.label}
                            </FilterChip>
                        );
                    })}
                </FilterGroup>
            </Controls>

            <CalendarContainer $isDark={isDark}>
                <CalendarGrid>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <DayHeader key={day} $isDark={isDark}>{day}</DayHeader>
                    ))}

                    {days.map((day, index) => {
                        const dayActivities = getActivitiesForDate(day.fullDate);
                        const isTodayDate = isToday(day.fullDate);

                        return (
                            <DayCell
                                key={index}
                                isToday={isTodayDate}
                                isOtherMonth={day.isOtherMonth}
                                $isDark={isDark}
                            >
                                <div className="day-number">{day.date}</div>
                                {dayActivities.slice(0, 3).map(activity => {
                                    const config = ACTIVITY_TYPES[activity.type];
                                    const Icon = config.icon;
                                    return (
                                        <ActivityItem key={activity.id} color={config.color} $isDark={isDark}>
                                            <Icon />
                                            <span className="activity-title">{activity.title}</span>
                                        </ActivityItem>
                                    );
                                })}
                                {dayActivities.length > 3 && (
                                    <div style={{ fontSize: 11, color: isDark ? '#9ca3af' : 'var(--gray-500)', marginTop: 4, paddingLeft: 4 }}>
                                        +{dayActivities.length - 3} more
                                    </div>
                                )}
                            </DayCell>
                        );
                    })}
                </CalendarGrid>

                <Legend $isDark={isDark}>
                    {Object.entries(ACTIVITY_TYPES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                            <div key={key} className="legend-item">
                                <div className="color-box" style={{ background: config.color }} />
                                <Icon style={{ width: 14, height: 14 }} />
                                {config.label}
                            </div>
                        );
                    })}
                </Legend>
            </CalendarContainer>
            </CardContainer>
        </PageContainer>
    );
};

export default MarketingCalendar;
